import { useDelete, useGetIdentity, useInvalidate } from "@refinedev/core";
import { useTable } from "@refinedev/antd";
import { Alert, App, Button, Card, Grid } from "antd";
import { useState, type FC } from "react";
import type { Environment, Project, User } from "../../types";
import { axiosInstance } from "../../api/client";
import { GlobalOutlined } from "@ant-design/icons";
import { AddProjectDrawer } from "./_components/add-project-drawer";
import { CredentialsDrawer } from "./_components/credentials-drawer";
import { EditProjectDrawer } from "./_components/edit-project-drawer";
import { EnvironmentDrawer } from "./_components/environment-drawer";
import { ProjectCard } from "./_components/project-card";

export const ProjectList: FC = () => {
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [envDrawer, setEnvDrawer] = useState<{ open: boolean; projectId: string | null }>({
    open: false,
    projectId: null,
  });
  const [credDrawer, setCredDrawer] = useState<{ open: boolean; projectId: string | null; envId: string | null }>({
    open: false,
    projectId: null,
    envId: null,
  });

  const invalidate = useInvalidate();
  const { modal } = App.useApp();

  const { data: identity } = useGetIdentity<User>();
  const breakpoint = Grid.useBreakpoint();
  const isMobile = !breakpoint.sm;
  const { tableProps } = useTable<Project>({ syncWithLocation: false });
  const { mutate: deleteProject } = useDelete();

  const handleProjectMenuClick = (project: Project, key: string) => {
    if (key === "edit") {
      setEditProject(project);
    } else if (key === "remove") {
      modal.confirm({
        title: "Remove project",
        content: "Are you sure you want to remove this project? All environments will be deleted.",
        okText: "Remove",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        onOk: () => deleteProject({ resource: "projects", id: project.id }),
      });
    }
  };

  const handleDeleteEnv = async (projectId: string, envId: string) => {
    await axiosInstance.delete(`/projects/${projectId}/environments/${envId}`);
    await invalidate({ resource: "projects", invalidates: ["list"] });
  };

  const handleEnvMenuClick = (projectId: string, env: Environment, key: string) => {
    if (key === "credentials") {
      setCredDrawer({ open: true, projectId, envId: env.id });
    } else if (key === "remove") {
      modal.confirm({
        title: "Remove environment",
        content: "Remove this environment?",
        okText: "Remove",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        onOk: () => handleDeleteEnv(projectId, env.id),
      });
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>Projects</h2>
        <Button type="primary" block={isMobile} onClick={() => setAddProjectOpen(true)}>
          Add project
        </Button>
      </div>

      {identity?.plan === "free" && (
        <Alert
          type="info"
          showIcon
          message="Project limit (free during alpha)"
          description="The free plan will be limited to 3 projects. Enjoy unlimited projects for free while Loopress is in alpha."
          style={{ marginBottom: 16 }}
        />
      )}

      {(tableProps.dataSource ?? []).length === 0 ? (
        <Card style={{ textAlign: "center", padding: "48px 0" }}>
          <div style={{ marginBottom: 16 }}>
            <GlobalOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />
          </div>
          <p style={{ fontWeight: 600, marginBottom: 8, fontSize: 18 }}>No projects yet</p>
          <p style={{ color: "#888", marginBottom: 24 }}>
            Add a WordPress site to start syncing its config across environments.
          </p>
          <Button type="primary" onClick={() => setAddProjectOpen(true)}>
            Add your first project
          </Button>
        </Card>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 380px), 1fr))", gap: 16 }}>
          {(tableProps.dataSource ?? []).map((project: Project) => (
            <ProjectCard
              key={project.id}
              project={project}
              isMobile={isMobile}
              onProjectMenuClick={handleProjectMenuClick}
              onEnvMenuClick={handleEnvMenuClick}
              onAddEnvironment={(projectId) => setEnvDrawer({ open: true, projectId })}
            />
          ))}
        </div>
      )}

      <AddProjectDrawer open={addProjectOpen} onClose={() => setAddProjectOpen(false)} isMobile={isMobile} />

      <EditProjectDrawer project={editProject} onClose={() => setEditProject(null)} isMobile={isMobile} />

      <EnvironmentDrawer
        projectId={envDrawer.projectId}
        open={envDrawer.open}
        onClose={() => setEnvDrawer({ open: false, projectId: null })}
        isMobile={isMobile}
      />

      <CredentialsDrawer
        projectId={credDrawer.projectId}
        envId={credDrawer.envId}
        open={credDrawer.open}
        onClose={() => setCredDrawer({ open: false, projectId: null, envId: null })}
        isMobile={isMobile}
      />
    </>
  );
};
